import React from 'react';
import { Loader2, Pencil, Plus, Star, Trash2, X } from 'lucide-react';
import { getAuthHeaders } from '../../stores/authStore';
import { cn, ui } from '../../utils';
import type { PromptTemplate } from '../../types';

interface PromptTemplatesButtonProps {
  disabled?: boolean;
  onInsert: (content: string) => void;
  buttonClassName?: string;
}

function normalizeTemplate(input: any): PromptTemplate {
  return {
    id: String(input?.id ?? crypto.randomUUID()),
    userId: input?.userId ?? input?.user_id ?? null,
    userEmail: input?.userEmail ?? input?.user_email ?? null,
    name: String(input?.name ?? 'Template').trim() || 'Template',
    content: String(input?.content ?? ''),
    createdAt: input?.createdAt ?? input?.created_at ?? new Date().toISOString(),
  };
}

function getTemplateArray(payload: any): PromptTemplate[] {
  const rawTemplates = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.templates)
      ? payload.templates
      : Array.isArray(payload?.data)
        ? payload.data
        : [];

  return rawTemplates.map((template: any) => normalizeTemplate(template));
}

export function PromptTemplatesButton({ disabled = false, onInsert, buttonClassName }: PromptTemplatesButtonProps) {
  const [isOpen, setIsOpen] = React.useState(false);
  const [templates, setTemplates] = React.useState<PromptTemplate[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [isSaving, setIsSaving] = React.useState(false);
  const [deletingTemplateId, setDeletingTemplateId] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [editingTemplateId, setEditingTemplateId] = React.useState<string | null>(null);
  const [draftName, setDraftName] = React.useState('');
  const [draftContent, setDraftContent] = React.useState('');
  const panelRef = React.useRef<HTMLDivElement>(null);

  const resetDraft = React.useCallback(() => {
    setEditingTemplateId(null);
    setDraftName('');
    setDraftContent('');
  }, []);

  const loadTemplates = React.useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch('/api/prompt-templates', {
        headers: getAuthHeaders({ includeImpersonation: true }),
      });
      const payload = await response.json().catch(() => null);

      if (!response.ok) {
        throw new Error(payload?.error || 'Failed to load templates');
      }

      setTemplates(getTemplateArray(payload));
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : 'Failed to load templates');
    } finally {
      setIsLoading(false);
    }
  }, []);

  React.useEffect(() => {
    if (!isOpen) {
      return;
    }

    void loadTemplates();
  }, [isOpen, loadTemplates]);

  React.useEffect(() => {
    if (!isOpen) {
      resetDraft();
      setError(null);
    }
  }, [isOpen, resetDraft]);

  React.useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handlePointerDown = (event: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [isOpen]);

  const handleSave = React.useCallback(async () => {
    const trimmedName = draftName.trim();
    if (!trimmedName || !draftContent.trim() || isSaving) {
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      const response = await fetch(editingTemplateId ? `/api/prompt-templates/${editingTemplateId}` : '/api/prompt-templates', {
        method: editingTemplateId ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders({ includeImpersonation: true }),
        },
        body: JSON.stringify({ name: trimmedName, content: draftContent }),
      });
      const payload = await response.json().catch(() => null);

      if (!response.ok) {
        throw new Error(payload?.error || 'Failed to save template');
      }

      const template = normalizeTemplate(payload?.data ?? payload?.template ?? payload);
      setTemplates((current) => {
        const next = editingTemplateId
          ? current.map((entry) => (entry.id === template.id ? template : entry))
          : [...current, template];
        return [...next].sort((left, right) => left.name.localeCompare(right.name));
      });
      resetDraft();
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : 'Failed to save template');
    } finally {
      setIsSaving(false);
    }
  }, [draftContent, draftName, editingTemplateId, isSaving, resetDraft]);

  const handleDelete = React.useCallback(async (templateId: string) => {
    if (deletingTemplateId) {
      return;
    }

    setDeletingTemplateId(templateId);
    setError(null);

    try {
      const response = await fetch(`/api/prompt-templates/${templateId}`, {
        method: 'DELETE',
        headers: getAuthHeaders({ includeImpersonation: true }),
      });
      const payload = await response.json().catch(() => null);

      if (!response.ok) {
        throw new Error(payload?.error || 'Failed to delete template');
      }

      setTemplates((current) => current.filter((template) => template.id !== templateId));
      if (editingTemplateId === templateId) {
        resetDraft();
      }
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : 'Failed to delete template');
    } finally {
      setDeletingTemplateId(null);
    }
  }, [deletingTemplateId, editingTemplateId, resetDraft]);

  const beginEdit = React.useCallback((template: PromptTemplate) => {
    setEditingTemplateId(template.id);
    setDraftName(template.name);
    setDraftContent(template.content);
    setError(null);
  }, []);

  const handleInsert = React.useCallback((template: PromptTemplate) => {
    onInsert(template.content);
    setIsOpen(false);
  }, [onInsert]);

  return (
    <div className="relative" ref={panelRef}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setIsOpen((open) => !open)}
        className={cn(
          ui.button.base,
          disabled ? 'cursor-not-allowed border-border bg-background/70 text-muted-foreground' : ui.button.secondary,
          'px-3',
          buttonClassName,
        )}
        aria-label="Open prompt templates"
        aria-haspopup="dialog"
        aria-expanded={isOpen}
      >
        <Star className="h-4 w-4" />
        <span>Templates</span>
      </button>

      {isOpen ? (
        <div className="absolute bottom-[calc(100%+0.75rem)] right-0 z-50 w-[min(32rem,calc(100vw-2rem))] overflow-hidden rounded-[1.75rem] border border-border bg-card shadow-2xl shadow-black/30" role="dialog" aria-label="Prompt templates">
          <div className="flex items-center justify-between border-b border-border px-4 py-3">
            <div>
              <div className="text-sm font-semibold text-foreground">Prompt templates</div>
              <div className="text-xs text-muted-foreground">Save reusable prompts, then tap one to drop it into the composer.</div>
            </div>
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className={cn(ui.button.icon, 'h-10 w-10')}
              aria-label="Close templates"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="grid max-h-[70vh] gap-0 overflow-hidden md:grid-cols-[1.1fr_0.9fr]">
            <div className="border-b border-border md:border-b-0 md:border-r">
              <div className="px-4 py-3 text-xs font-medium uppercase tracking-wide text-muted-foreground">Saved templates</div>
              <div className="max-h-[40vh] space-y-2 overflow-y-auto px-4 pb-4">
                {isLoading ? (
                  <div className="flex items-center gap-2 rounded-2xl border border-border bg-background/70 px-4 py-3 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" /> Loading templates...
                  </div>
                ) : templates.length === 0 ? (
                  <div className={cn(ui.emptyState, 'px-4 py-8')}>
                    <div className="text-sm font-medium text-foreground-bright">No templates yet</div>
                    <div className="mt-2 text-sm text-muted-foreground">Create one on the right, then tap it to drop it into the composer.</div>
                  </div>
                ) : (
                  templates.map((template) => (
                    <div key={template.id} className="rounded-2xl border border-border bg-background/70 p-3 transition-colors duration-150 hover:bg-card">
                      <div className="flex items-start justify-between gap-3">
                        <button
                          type="button"
                          onClick={() => handleInsert(template)}
                          className="min-w-0 flex-1 text-left"
                          aria-label={`Insert template ${template.name}`}
                        >
                          <div className="truncate text-sm font-medium text-foreground">{template.name}</div>
                          <div className="mt-1 line-clamp-3 text-xs leading-5 text-muted-foreground">{template.content}</div>
                        </button>
                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            onClick={() => beginEdit(template)}
                            className={cn(ui.button.icon, 'h-9 w-9')}
                            title={`Edit ${template.name}`}
                            aria-label={`Edit ${template.name}`}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => void handleDelete(template.id)}
                            disabled={deletingTemplateId === template.id}
                            className={cn(ui.button.icon, 'h-9 w-9 hover:text-red-300 disabled:opacity-60')}
                            title={`Delete ${template.name}`}
                            aria-label={`Delete ${template.name}`}
                          >
                            {deletingTemplateId === template.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                          </button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="flex flex-col">
              <div className="px-4 py-3 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                {editingTemplateId ? 'Edit template' : 'New template'}
              </div>
              <div className="space-y-3 px-4 pb-4">
                <input
                  type="text"
                  value={draftName}
                  onChange={(event) => setDraftName(event.target.value)}
                  placeholder="Template name"
                  maxLength={120}
                  className={cn(ui.input, 'rounded-2xl px-3 py-2')}
                />
                <textarea
                  value={draftContent}
                  onChange={(event) => setDraftContent(event.target.value)}
                  placeholder="Write the prompt you want to reuse..."
                  rows={8}
                  className={cn(ui.textarea, 'rounded-2xl px-3 py-2')}
                />
                {error ? (
                  <div className="rounded-2xl border border-red-500/20 bg-red-500/10 px-3 py-2 text-xs text-red-200 shadow-sm shadow-red-950/10">{error}</div>
                ) : null}
                <div className="flex flex-wrap justify-between gap-2">
                  <button
                    type="button"
                    onClick={resetDraft}
                    className={cn(ui.button.base, ui.button.ghost, 'px-3 py-2 text-xs')}
                  aria-label={editingTemplateId ? 'Cancel template edit' : 'Clear template form'}
                  >
                    {editingTemplateId ? 'Cancel edit' : 'Clear'}
                  </button>
                  <button
                    type="button"
                    onClick={() => void handleSave()}
                    disabled={!draftName.trim() || !draftContent.trim() || isSaving}
                    className={cn(ui.button.base, ui.button.primary, 'px-4 py-2.5')}
                    aria-label={editingTemplateId ? 'Update template' : 'Save template'}
                  >
                    {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                    {editingTemplateId ? 'Update template' : 'Save template'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default PromptTemplatesButton;
